import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Suspense,
  startTransition,
} from 'react';
import { useSelector } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from './store';
import {
  fetchDatabaseFilters,
  fetchFavoritesThunk,
  fetchPreviousResults,
  fetchDatabaseStats,
  screenStocksThunk,
} from './store/thunks';
import { hideGlobalMessage } from './store/slices/globalMessageSlice';
import { fetchBillingMe } from './store/slices/billingSlice';
import {
  closePanel,
  openPanel,
  type AnalysisPanelTab,
} from './store/slices/analysisPanelSlice';
import {
  clearDeepAnalysisReturnTab,
  clearPendingDeepSubTab,
  clearPendingMainTab,
  clearPendingMobileDeepSection,
  requestDeepSubTab,
  requestNavigateMainTab,
  setMobileDeepSection,
  type DeepSubTab,
  type MobileDeepSection,
} from './store/slices/modalsSlice';
import { setChartTicker } from './store/slices/chartSlice';
import { setSelectedTicker } from './store/slices/analysisPanelSlice';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import LanguageProvider from './contexts/LanguageProvider';
import { getDirection } from './translations';
import useAuth from './hooks/useAuth';
import ActionButtons from './components/ActionButtons';
import ResultsTableVirtualized from './components/ResultsTableVirtualized';
import FavoritesTab from './components/FavoritesTab';
import AlertsTab from './components/AlertsTab';
import GlobalMessage from './components/GlobalMessage';
import LazyLoadingFallback from './components/common/LazyLoadingFallback';
import LazyErrorBoundary from './components/common/LazyErrorBoundary';
import SkipLink from './components/common/SkipLink';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { AccessibilityToolbar } from './components/AccessibilityToolbar';
import SEO from './components/SEO';
import { performanceMonitor } from './utils/performanceMonitor';
import { initializePreloaders } from './hooks/usePreloader';
import useBrowserNavigationSync from './hooks/useBrowserNavigationSync';
import {
  PageBackground,
  DesktopSidebar,
  MobileBottomTabs,
  SystemStatusBar,
  Footer,
  type TabId,
  isValidTabId,
  SETTINGS_SECTION_STORAGE_KEY,
} from './components/layouts';
import { cn } from './lib/utils';
import { BRAND_NAME } from './config/branding';
import {
  isChartsGridEnabled,
  isHelpSystemEnabled,
} from './config/featureFlags';
import { fetchPositionDiaryFeatureStatus } from './store/thunks/positionsThunks';
import ChartsGridTab from './components/ChartsGridTab';
import {
  HelpCenterModal,
  HelpTab,
  OnboardingChecklistCard,
  OnboardingTourOverlay,
  OnboardingWelcomeModal,
} from './components/Help';
// Eagerly loaded - avoids Suspense flash when opening the dialog
import FinvizScreenerModal from './components/FinvizScreenerModal';
import TrialCountdownBadge from './components/Billing/TrialCountdownBadge';
import TrialExpiredWall from './components/Billing/TrialExpiredWall';
import './App.css';

const ACTIVE_TAB_STORAGE_KEY = 'stock_screener_active_tab';
const DEEP_SUB_TAB_STORAGE_KEY = 'deepSubTab_v1';
const MOBILE_DEEP_SECTION_STORAGE_KEY = 'mobileDeepSection_v1';

// Map the legacy 'billing' tab id onto Settings → Billing section. Keeps
// `?tab=billing` deep-links and TrialCountdownBadge's
// requestNavigateMainTab('billing') working without touching those callers.
function resolveTabAlias(id: TabId): TabId {
  if (id === 'billing') {
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(SETTINGS_SECTION_STORAGE_KEY, 'billing');
      } catch {
        /* sessionStorage unavailable - section falls back to default */
      }
    }
    return 'settings';
  }
  return id;
}

function loadDeepSubTab(): DeepSubTab {
  if (typeof sessionStorage === 'undefined') return 'analysis';
  const v = sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY);
  if (v === 'analysis' || v === 'chart') return v;
  return 'analysis';
}

function loadMobileDeepSection(): MobileDeepSection {
  if (typeof sessionStorage === 'undefined') return 'overview';
  const v = sessionStorage.getItem(MOBILE_DEEP_SECTION_STORAGE_KEY);
  if (
    v === 'search' ||
    v === 'overview' ||
    v === 'live-chart' ||
    v === 'analyze-chart' ||
    v === 'analyze-details'
  ) {
    return v;
  }
  // Migration from legacy desktop key.
  const legacy = sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY);
  if (legacy === 'chart') return 'live-chart';
  return 'analyze-details';
}

/**
 * Phase 3 (homepage rewrite): allow callers to deep-link into a specific
 * tab via `?tab=<id>`. Used by AgentsSection / LoginModal to hand off
 * an authenticated user to the Agent Console after sign-in. Returns
 * null when the param is absent or its value isn't in the TabId
 * allowlist (`isValidTabId` is the single source of truth - see
 * components/layouts/navigationConfig.ts).
 */
function readTabFromQuery(): TabId | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('tab');
    if (!raw) return null;
    return isValidTabId(raw) ? raw : null;
  } catch {
    return null;
  }
}

// Lazy load heavy components with performance monitoring
const AnalysisPanel = React.lazy(
  performanceMonitor.measureLazyLoad(
    'AnalysisPanel',
    () => import('./components/AnalysisPanel')
  )
);

const ChartModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'ChartModal',
    () => import('./components/ChartModal')
  )
);

const AIAnalysisModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'AIAnalysisModal',
    () => import('./components/AIAnalysisModal')
  )
);

const TradingOpportunitiesTab = React.lazy(
  performanceMonitor.measureLazyLoad(
    'TradingOpportunitiesTab',
    () => import('./components/TradingOpportunitiesTab')
  )
);

const DeepAnalysisContainer = React.lazy(
  performanceMonitor.measureLazyLoad(
    'DeepAnalysisContainer',
    () => import('./components/DeepAnalysisContainer')
  )
);

const AdminHub = React.lazy(
  performanceMonitor.measureLazyLoad(
    'AdminHub',
    () => import('./components/AdminHub')
  )
);

const AgentConsole = React.lazy(
  performanceMonitor.measureLazyLoad(
    'AgentConsole',
    () => import('./components/AgentConsole')
  )
);

const SettingsPage = React.lazy(
  performanceMonitor.measureLazyLoad(
    'SettingsPage',
    () => import('./components/Settings/SettingsPage')
  )
);

const UserRoleRequest = React.lazy(
  performanceMonitor.measureLazyLoad(
    'UserRoleRequest',
    () => import('./components/UserRoleRequest')
  )
);

const FiltersModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'FiltersModal',
    () => import('./components/FiltersModal')
  )
);

const LegacyFiltersModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'LegacyFiltersModal',
    () => import('./components/LegacyFiltersModal')
  )
);

const UpgradePromptModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'UpgradePromptModal',
    () => import('./components/UpgradePromptModal')
  )
);

const AboutModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'AboutModal',
    () => import('./components/AboutModal')
  )
);

const ContactSupportModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'ContactSupportModal',
    () => import('./components/ContactSupportModal')
  )
);

const FeedbackModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'FeedbackModal',
    () => import('./components/FeedbackModal')
  )
);

const TermsModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'TermsModal',
    () => import('./components/TermsModal')
  )
);

const PrivacyModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'PrivacyModal',
    () => import('./components/PrivacyModal')
  )
);

const DisclaimerModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'DisclaimerModal',
    () => import('./components/DisclaimerModal')
  )
);

const DisclaimerGateModal = React.lazy(
  performanceMonitor.measureLazyLoad(
    'DisclaimerGateModal',
    () => import('./components/Auth/DisclaimerGateModal')
  )
);

const DiaryPage = React.lazy(
  performanceMonitor.measureLazyLoad(
    'DiaryPage',
    () => import('./components/DiaryTrading/DiaryPage')
  )
);

function App() {
  const dispatch = useAppDispatch();
  const globalMessage = useSelector((state: RootState) => state.globalMessage);
  const screening = useSelector((state: RootState) => state.screening);
  const analysisPanel = useSelector((state: RootState) => state.analysisPanel);
  const modals = useSelector((state: RootState) => state.modals);
  const { isAuthenticated, loading: authLoading, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const direction = getDirection(i18n.language);

  const canAccessAdmin = isAdmin;
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    // Phase 3 deep-link: `?tab=<id>` wins over sessionStorage and the
    // test-env default so AgentsSection -> LoginModal can drop an
    // authenticated user straight into the Agent Console (or any other
    // valid tab). The query is stripped by an effect below so a refresh
    // doesn't re-trigger the redirect.
    const fromQuery = readTabFromQuery();
    if (fromQuery) return resolveTabAlias(fromQuery);
    if (process.env.NODE_ENV === 'test') return 'screening';
    if (typeof sessionStorage === 'undefined') return 'screening';
    const savedTab = sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY);

    // Migration A: legacy direct tab IDs (chart_view / deep_analysis as activeTab from before merge wave)
    if (savedTab === 'chart_view') {
      sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'deep_analysis');
      if (!sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY)) {
        sessionStorage.setItem(DEEP_SUB_TAB_STORAGE_KEY, 'chart');
      }
      return 'deep_analysis';
    }
    if (savedTab === 'deep_analysis') {
      if (!sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY)) {
        sessionStorage.setItem(DEEP_SUB_TAB_STORAGE_KEY, 'analysis');
      }
      return 'deep_analysis';
    }

    // Migration B: intermediate v2 chartsSubTab_v1 (set by the prior merge swarm)
    if (savedTab === 'charts') {
      const legacySub = sessionStorage.getItem('chartsSubTab_v1');
      if (legacySub === 'chart') {
        sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'deep_analysis');
        if (!sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY)) {
          sessionStorage.setItem(DEEP_SUB_TAB_STORAGE_KEY, 'chart');
        }
        sessionStorage.removeItem('chartsSubTab_v1');
        return 'deep_analysis';
      }
      if (legacySub === 'deep') {
        sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, 'deep_analysis');
        if (!sessionStorage.getItem(DEEP_SUB_TAB_STORAGE_KEY)) {
          sessionStorage.setItem(DEEP_SUB_TAB_STORAGE_KEY, 'analysis');
        }
        sessionStorage.removeItem('chartsSubTab_v1');
        return 'deep_analysis';
      }
      if (legacySub === 'grid') {
        sessionStorage.removeItem('chartsSubTab_v1');
        return 'charts';
      }
    }

    return savedTab && isValidTabId(savedTab) ? resolveTabAlias(savedTab) : 'screening';
  });
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [previousResultsFetched, setPreviousResultsFetched] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isTestEnv = process.env.NODE_ENV === 'test';

  // Phase 3: after the activeTab initializer has consumed any `?tab=`
  // query param, scrub it from the URL so a refresh doesn't redirect
  // again. Single-shot (empty deps) and idempotent - `replaceState` is
  // a no-op when the URL is already clean.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('tab')) return;
      params.delete('tab');
      const remaining = params.toString();
      const next =
        window.location.pathname +
        (remaining ? `?${remaining}` : '') +
        window.location.hash;
      // Preserve existing history.state (useBrowserNavigationSync's nav descriptor
      // lives there) - replacing it with {} would drop Back/Forward state.
      window.history.replaceState({ ...(window.history.state ?? {}) }, '', next);
    } catch {
      // History API unavailable (rare in jsdom edge cases) - non-critical.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep Analysis sub-tab - App owns the authoritative state. The container
  // (DeepAnalysisContainer) is a controlled component receiving `subTab` +
  // `setSubTab` as props. The Redux `pendingDeepSubTab` action is used ONLY
  // for cross-component requests (e.g. ResultsTable Deep button, Charts grid
  // click); App observes it and applies the request, then clears.
  const [deepSubTab, _setDeepSubTab] = useState<DeepSubTab>(loadDeepSubTab);

  const setDeepSubTab = useCallback((t: DeepSubTab) => {
    _setDeepSubTab(t);
    try {
      sessionStorage.setItem(DEEP_SUB_TAB_STORAGE_KEY, t);
    } catch {
      // sessionStorage may be unavailable (private mode, etc.) - non-critical
    }
  }, []);

  // Apply external sub-tab requests dispatched via requestDeepSubTab,
  // then clear the pending field so the action is single-shot.
  const pendingSub = useSelector(
    (s: RootState) => s.modals.pendingDeepSubTab
  );
  const mobileDeepSection = useSelector(
    (s: RootState) => s.modals.mobileDeepSection
  );
  const pendingMobileSection = useSelector(
    (s: RootState) => s.modals.pendingMobileDeepSection
  );
  useEffect(() => {
    if (!pendingSub) return;
    setDeepSubTab(pendingSub);
    dispatch(clearPendingDeepSubTab());
    // Mobile-side mirroring of requestDeepSubTab is handled in
    // DeepAnalysisContainer so there is a single useIsMobile source of truth
    // (avoids two independent hook instances drifting on resize).
  }, [pendingSub, dispatch, setDeepSubTab]);

  // Apply external mobile-section requests, then clear so they're single-shot.
  useEffect(() => {
    if (!pendingMobileSection) return;
    dispatch(setMobileDeepSection(pendingMobileSection));
    dispatch(clearPendingMobileDeepSection());
  }, [pendingMobileSection, dispatch]);

  // Hydrate mobile section from sessionStorage on first mount.
  useEffect(() => {
    const loaded = loadMobileDeepSection();
    dispatch(setMobileDeepSection(loaded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist mobile section to sessionStorage on change.
  useEffect(() => {
    try {
      sessionStorage.setItem(MOBILE_DEEP_SECTION_STORAGE_KEY, mobileDeepSection);
    } catch {
      // private mode / storage disabled - non-critical
    }
  }, [mobileDeepSection]);

  // Browser Back/Forward sync for in-app navigation (tabs + analysis panel).
  useBrowserNavigationSync({
    activeTab,
    setActiveTab,
    isAuthenticated,
    isAdmin,
    deepSubTab,
    setDeepSubTab,
  });

  // Handle tab change and close analysis panel when switching tabs
  const handleTabChange = (newTab: TabId) => {
    // Clear any Deep Analysis return-tab marker when navigating away from Deep Analysis.
    if (newTab !== 'deep_analysis') {
      dispatch(clearDeepAnalysisReturnTab());
    }
    // Preserve ticker context - closePanel resets selectedTicker.
    const tickerForNav = analysisPanel.selectedTicker;
    if (analysisPanel.isOpen) {
      dispatch(closePanel());
    }
    if (newTab === 'deep_analysis' && tickerForNav) {
      dispatch(setSelectedTicker(tickerForNav));
    }
    // Use startTransition to prevent Suspense errors during synchronous tab switching
    // startTransition marks setActiveTab as a non-urgent update that can be interrupted
    startTransition(() => {
      setActiveTab(newTab);
    });
  };

  // In-app navigation from modals/panels (e.g. TV → Selected chart tab).
  // Preserve selectedTicker across closePanel - the panel slice wipes
  // selectedTicker on close, but Deep Analysis needs it to drive the Analysis
  // sub-tab content (otherwise sub-tab switch from Chart → Analysis shows the
  // empty-ticker placeholder).
  useEffect(() => {
    const next = modals.pendingMainTab;
    if (!next) return;
    if (!isValidTabId(next)) {
      dispatch(clearPendingMainTab());
      return;
    }
    dispatch(clearPendingMainTab());
    const resolved = resolveTabAlias(next as TabId);
    const tickerForNav = analysisPanel.selectedTicker;
    if (analysisPanel.isOpen) {
      dispatch(closePanel());
    }
    if (resolved === 'deep_analysis' && tickerForNav) {
      dispatch(setSelectedTicker(tickerForNav));
    }
    startTransition(() => {
      setActiveTab(resolved);
    });
  }, [modals.pendingMainTab, dispatch, analysisPanel.isOpen, analysisPanel.selectedTicker]);

  // Trading tab is disabled for now - redirect to screening if landing on it
  useEffect(() => {
    if (activeTab === 'trading') {
      setActiveTab('screening');
    }
  }, [activeTab]);

  // Keep the selected tab stable across auth/component remounts.
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    }
  }, [activeTab]);

  // Load initial data AFTER auth is ready to ensure user-specific data is loaded
  useEffect(() => {
    // Wait for auth to be ready before loading user-specific data
    if (authLoading) {
      return;
    }

    // Initialize preloaders for lazy components
    initializePreloaders();

    // Load initial data - use database-backed filter API as single source of truth
    // This must run AFTER auth is ready so the auth token is available for user-specific config
    dispatch(fetchDatabaseFilters());
    dispatch(fetchDatabaseStats(false));
    dispatch(fetchFavoritesThunk());
    // Position Diary feature-status probe (unauthenticated, fires at boot).
    // The thunk writes positionsSlice.featureEnabled; the nav guard reads the
    // slice state at render time via isPositionDiaryEnabled() + the featureEnabled
    // prop flow (App re-renders when the slice updates).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(fetchPositionDiaryFeatureStatus() as any);
    // Load previous screening results from database (user-specific).
    // Track terminal settlement so auto-scan effect knows it's safe to read results.
    const p = dispatch(fetchPreviousResults()) as unknown as Promise<unknown>;
    p.finally(() => setPreviousResultsFetched(true));
    setInitialDataLoaded(true);
  }, [dispatch, authLoading]);

  // Entitlement (paid/trial/comp) — only when signed in. Anon calls 401 and
  // would wipe trial state; after logout/login the badge stayed hidden until refresh.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    dispatch(fetchBillingMe());
  }, [authLoading, isAuthenticated, dispatch]);

  // Re-fetch user-specific data when auth state changes (user logs in)
  useEffect(() => {
    if (initialDataLoaded && !authLoading && isAuthenticated) {
      // Reset settlement flag so auto-scan waits for the fresh fetch to finish.
      setPreviousResultsFetched(false);
      dispatch(fetchDatabaseFilters());
      dispatch(fetchFavoritesThunk());
      const p = dispatch(fetchPreviousResults()) as unknown as Promise<unknown>;
      p.finally(() => setPreviousResultsFetched(true));
    }
  }, [isAuthenticated, authLoading, dispatch, initialDataLoaded]);

  // Auto-start screening once per session after login when no prior results exist.
  // First-time users (empty results) see fresh data; returning users keep cached
  // last run. Gated by sessionStorage flag (cleared on logout) so refresh does not
  // re-kick a scan.
  useEffect(() => {
    const AUTO_SCAN_FLAG_KEY = 'auto_scan_done_this_session';
    if (authLoading || !isAuthenticated || !initialDataLoaded) return;
    // Must wait for fetchPreviousResults to settle - initial screening.status='idle'
    // is the default state, not a confirmation that "no results exist".
    if (!previousResultsFetched) return;
    if (typeof sessionStorage === 'undefined') return;
    if (sessionStorage.getItem(AUTO_SCAN_FLAG_KEY) === '1') return;

    const status = screening?.status;
    if (status === 'running' || status === 'loading') return;
    if ((screening?.results?.length ?? 0) > 0) {
      // Prior results exist - honor them, do not auto-scan.
      sessionStorage.setItem(AUTO_SCAN_FLAG_KEY, '1');
      return;
    }

    sessionStorage.setItem(AUTO_SCAN_FLAG_KEY, '1');
    console.log('🚀 Auto-starting screening on login (no prior results)');
    (dispatch(screenStocksThunk()) as unknown as Promise<unknown>).catch(
      (err) => {
        console.warn('Auto-scan on login failed:', err);
      }
    );
  }, [
    isAuthenticated,
    authLoading,
    initialDataLoaded,
    previousResultsFetched,
    screening?.status,
    screening?.results?.length,
    dispatch,
  ]);

  // Deep-link handler: honor ?ticker=AAPL&tab=deep (or legacy &tab=view) from
  // email/WhatsApp alerts. Fires once after initial data + auth are ready,
  // then strips the params so a manual reload doesn't re-open the panel
  // unexpectedly.
  //
  // URL contract (public): ?ticker={TICKER}&tab={alias}&source=alert
  //   tab='deep'         → Deep Analysis main tab (analysis sub-tab) - current default for new alerts.
  //   tab='view'         → AnalysisPanel modal, TradingView sub-tab - legacy alias kept for emails already in inboxes.
  //   tab='chart','ai','details','manual-analysis' → AnalysisPanel modal sub-tabs.
  //
  // Note on race: `initialDataLoaded` is set synchronously after the initial
  // thunks are *dispatched*, not after they resolve. The `view` (TradingView
  // widget) tab is self-contained and doesn't need screening data, so this
  // is fine today. If future deep links target data-dependent tabs
  // (`details`, `ai`, etc.), gate those on the relevant slice populating.
  const deepLinkConsumed = useRef(false);
  useEffect(() => {
    if (deepLinkConsumed.current) return;
    if (!initialDataLoaded || authLoading) return;

    const params = new URLSearchParams(window.location.search);
    const rawTicker = params.get('ticker');

    // If there's no ticker param at all, nothing to consume - bail without
    // touching the URL so unrelated query strings are preserved.
    if (!rawTicker) {
      deepLinkConsumed.current = true;
      return;
    }

    // We found a ticker param. Whether it's valid or not, we're going to
    // consume it and strip the deep-link params from the URL, so invalid
    // links don't keep re-triggering on refresh. Run the strip FIRST so
    // every exit path below cleans up.
    deepLinkConsumed.current = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('ticker');
      url.searchParams.delete('tab');
      url.searchParams.delete('source');
      // Preserve existing history.state (e.g. appNav from useBrowserNavigationSync).
      const prevHistoryState = window.history.state ?? {};
      window.history.replaceState(
        { ...prevHistoryState },
        '',
        url.pathname + url.search + url.hash
      );
    } catch {
      // replaceState isn't critical - continue processing the deep link
    }

    // Validate ticker: leading letter, up to 5 alphanumerics, with an optional
    // single `.` or `-` separator followed by 1-4 more alphanumerics (covers
    // BRK.B, RY-PT, etc.). Rejects all-dots/all-hyphens and anything longer
    // than a real ticker.
    //
    // NOTE: This regex MUST stay in lock-step with the backend validator in
    // src/stock_screener/services/alert_email_service.py. If the two drift,
    // emails may contain deep-links that silently do nothing on click.
    const ticker = rawTicker.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{0,4}(?:[-.][A-Z0-9]{1,4})?$/.test(ticker)) {
      return;
    }

    // tab='deep' takes a different code path: navigate to the Deep Analysis
    // MAIN tab pre-loaded with the ticker, instead of opening the AnalysisPanel
    // modal. Mirrors the StockActionModal "Microscope" button sequence.
    const rawTab = params.get('tab');
    if (rawTab === 'deep') {
      dispatch(setSelectedTicker(ticker));
      dispatch(setChartTicker(ticker));
      dispatch(requestDeepSubTab('analysis'));
      dispatch(requestNavigateMainTab('deep_analysis'));
      return;
    }

    // Map URL-facing tab aliases to internal AnalysisPanelTab names.
    // URL contract is deliberately vendor-neutral; internal slice names are
    // legacy and not user-visible.
    const urlTabMap: Record<string, AnalysisPanelTab> = {
      view: 'tradingview',
      chart: 'chart',
      ai: 'ai',
      details: 'details',
      'manual-analysis': 'manual-analysis',
    };
    const tab: AnalysisPanelTab =
      (rawTab && urlTabMap[rawTab]) || 'tradingview';

    // Land on the screening tab so the AnalysisPanel has context.
    startTransition(() => {
      setActiveTab('screening');
    });
    // Hardcode source='screening' rather than trusting the URL param - defense
    // in depth even though `source` isn't currently security-sensitive.
    dispatch(openPanel({ ticker, tab, source: 'screening' }));
  }, [initialDataLoaded, authLoading, dispatch]);

  useEffect(() => {
    // Auto-hide global messages after 2 seconds for success, 15 for others
    if (globalMessage.visible) {
      const timeout = globalMessage.type === 'success' ? 2000 : 15000;
      const timer = setTimeout(() => {
        dispatch(hideGlobalMessage());
      }, timeout);
      return () => clearTimeout(timer);
    }
    return () => {}; // Always return a cleanup function
  }, [globalMessage.visible, globalMessage.type, dispatch]);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AccessibilityProvider>
          <LanguageProvider>
            <SEO />
            <SkipLink />
            <div
              className="App dark overflow-x-hidden bg-bg-primary"
              lang={i18n.language}
              dir={direction}
            >
              {/* Screen-reader announcements for dynamic screening state */}
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              >
                {screening?.status === 'running'
                  ? screening?.message || 'Screening in progress.'
                  : screening?.status === 'completed'
                    ? screening?.message || 'Screening completed.'
                    : screening?.status === 'error'
                      ? screening?.message || 'Screening results are temporarily unavailable.'
                      : ''}
              </div>
              {/* Background Grid Pattern */}
              <PageBackground />

              {/* Global Messages */}
              <GlobalMessage />

              {/* Legacy <Header /> removed - SystemStatusBar is the active top bar */}

              {/* Desktop Sidebar Navigation */}
              <DesktopSidebar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                isAuthenticated={isAuthenticated}
                isAdmin={canAccessAdmin}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
              />

              {/* Main Content Area - flex-1 min-h-0 so only table scrolls on desktop */}
              <main
                id="main-content"
                className={cn(
                  'relative z-[1] flex-1 flex flex-col min-h-0',
                  // Native app-like: full width, proper padding for mobile
                  'px-0 md:px-2',
                  'pb-10 md:pb-0',
                  // Match DesktopSidebar w-16 / w-60 so content reflows when toggling
                  'md:transition-[margin] md:duration-200 md:ease-out',
                  sidebarCollapsed ? 'md:ms-16' : 'md:ms-60'
                )}
                tabIndex={-1}
              >
                {/* Visually hidden page heading for accessibility landmark / a11y tests */}
                <h1 className="sr-only">{BRAND_NAME}</h1>

                {/* System Status Bar */}
                <SystemStatusBar />

                {/* Trial countdown + escalation badge (PR-C3, in-app only).
                    Self-gates to trialing && !comped; renders nothing otherwise,
                    so it's safe to mount app-wide for authenticated users. */}
                {isAuthenticated && (
                  <div className="flex justify-end px-3 pt-1 md:px-4">
                    <TrialCountdownBadge />
                  </div>
                )}

                {/* Divider between status bar and toolbar */}
                <div className="h-px bg-border-subtle" />

                {/* Main Content Container */}
                <div
                  className={cn(
                    'w-full min-w-0 flex-1 flex flex-col min-h-0',
                    (activeTab === 'charts' && isChartsGridEnabled()) ||
                      activeTab === 'deep_analysis'
                      ? 'px-0 py-0 md:px-2 md:py-1'
                      : activeTab === 'screening'
                        ? 'px-3 py-1 md:px-4 md:py-4'
                        : activeTab === 'favorites' || activeTab === 'alerts'
                          ? 'px-3 py-1 md:px-4 md:py-4'
                          : 'px-3 py-4 md:px-4',
                    // Prevent horizontal scroll on all screens
                    'overflow-x-hidden',
                    // Screening: constrain height on mobile so the results list
                    // scrolls inside the card (not the whole page). On desktop
                    // allow the page itself to scroll vertically if the table
                    // chrome leaves too little room for the list - short laptop
                    // viewports (~700px) otherwise show only 2-3 rows.
                    activeTab === 'screening' &&
                      'max-md:overflow-hidden md:overflow-y-auto md:overflow-x-hidden',
                    (activeTab === 'favorites' || activeTab === 'alerts') &&
                      'md:overflow-hidden max-md:overflow-y-auto max-md:overscroll-contain',
                    /* Charts/Deep Analysis: inner panes own scrolling (avoids layout jump from double scrollbars). */
                    (activeTab === 'charts' || activeTab === 'deep_analysis') &&
                      'overflow-hidden',
                    activeTab !== 'screening' &&
                      activeTab !== 'charts' &&
                      activeTab !== 'deep_analysis' &&
                      activeTab !== 'favorites' &&
                      activeTab !== 'help' &&
                      activeTab !== 'alerts' &&
                      'space-y-4 overflow-auto'
                  )}
                >
                  {(activeTab === 'screening' ||
                    (activeTab === 'charts' && isChartsGridEnabled())) && (
                    <ActionButtons
                      toolbarVariant={
                        activeTab === 'charts' ? 'compact' : 'default'
                      }
                    />
                  )}

                  {/* Screening Tab */}
                  {activeTab === 'screening' && (
                    <div className="flex flex-col flex-1 min-h-0 max-md:overflow-hidden md:overflow-visible gap-0 md:gap-4">
                      {/* Flex layout: Table on left, Analysis Panel on right - table fills and scrolls */}
                      <div className="flex flex-col md:flex-row gap-0 md:gap-4 flex-1 min-h-0">
                        {/* Left: Results Table - fills available space */}
                        <div
                          className={cn(
                            'flex min-h-0 min-w-0 flex-1 flex-col max-md:overflow-hidden md:overflow-visible transition-all duration-300',
                            analysisPanel.isOpen
                              ? 'w-full md:w-1/2 md:min-w-[400px] max-md:min-w-0'
                              : 'w-full'
                          )}
                        >
                          <ResultsTableVirtualized />
                        </div>

                        {/* Right: Analysis Panel (static on desktop; full-screen overlay on mobile when open) */}
                        {analysisPanel.isOpen && (
                          <>
                            {/* Mobile: full-screen overlay with close */}
                            <div className="fixed inset-0 z-50 flex flex-col bg-bg-primary md:hidden">
                              <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-surface-container-low/95 shrink-0 min-h-[48px]">
                                <button
                                  type="button"
                                  onClick={() => dispatch(closePanel())}
                                  className="flex items-center gap-2 text-text-primary hover:text-accent-primary min-h-[44px] min-w-[44px]"
                                  aria-label="Close panel"
                                >
                                  <i className="fas fa-arrow-left" />
                                  <span className="text-sm font-medium">
                                    Back
                                  </span>
                                </button>
                                <span className="text-sm font-semibold text-text-primary truncate max-w-[180px]">
                                  {analysisPanel.selectedTicker}
                                </span>
                                <div className="w-14" />
                              </div>
                              <div className="flex-1 overflow-auto">
                                <LazyErrorBoundary componentName="Analysis Panel">
                                  <Suspense
                                    fallback={
                                      <LazyLoadingFallback componentName="Analysis Panel" />
                                    }
                                  >
                                    <AnalysisPanel
                                      key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                      panelVariant="mobile"
                                    />
                                  </Suspense>
                                </LazyErrorBoundary>
                              </div>
                            </div>
                            {/* Desktop: side panel */}
                            <div className="flex min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border-subtle md:max-h-full md:w-1/2 md:min-w-[500px] max-md:hidden">
                              <LazyErrorBoundary componentName="Analysis Panel">
                                <Suspense
                                  fallback={
                                    <LazyLoadingFallback componentName="Analysis Panel" />
                                  }
                                >
                                  <AnalysisPanel
                                    key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                    panelVariant="desktop"
                                  />
                                </Suspense>
                              </LazyErrorBoundary>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Favorites Tab */}
                  {activeTab === 'favorites' && (
                    <div className="animate-in flex flex-col flex-1 min-h-0 overflow-hidden">
                      {/* Flex layout: Table on left, Analysis Panel on right - stack on mobile */}
                      <div className="flex flex-col md:flex-row gap-2 md:gap-4 flex-1 min-h-0">
                        {/* Left: Favorites Table */}
                        <div
                          className={cn(
                            'transition-all duration-300 overflow-hidden flex-1 min-h-0 min-w-0',
                            analysisPanel.isOpen
                              ? 'w-full md:w-1/2 md:min-w-[400px] max-md:min-w-0'
                              : 'w-full'
                          )}
                        >
                          <FavoritesTab />
                        </div>

                        {/* Right: Analysis Panel (static on desktop; full-screen overlay on mobile when open) */}
                        {analysisPanel.isOpen && (
                          <>
                            <div className="fixed inset-0 z-50 flex flex-col bg-bg-primary md:hidden">
                              <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-surface-container-low/95 shrink-0 min-h-[48px]">
                                <button
                                  type="button"
                                  onClick={() => dispatch(closePanel())}
                                  className="flex items-center gap-2 text-text-primary hover:text-accent-primary min-h-[44px] min-w-[44px]"
                                  aria-label="Close panel"
                                >
                                  <i className="fas fa-arrow-left" />
                                  <span className="text-sm font-medium">
                                    Back
                                  </span>
                                </button>
                                <span className="text-sm font-semibold text-text-primary truncate max-w-[180px]">
                                  {analysisPanel.selectedTicker}
                                </span>
                                <div className="w-14" />
                              </div>
                              <div className="flex-1 overflow-auto">
                                <LazyErrorBoundary componentName="Analysis Panel">
                                  <Suspense
                                    fallback={
                                      <LazyLoadingFallback componentName="Analysis Panel" />
                                    }
                                  >
                                    <AnalysisPanel
                                      key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                      panelVariant="mobile"
                                    />
                                  </Suspense>
                                </LazyErrorBoundary>
                              </div>
                            </div>
                            <div className="flex min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border-subtle md:max-h-full md:w-1/2 md:min-w-[500px] max-md:hidden">
                              <LazyErrorBoundary componentName="Analysis Panel">
                                <Suspense
                                  fallback={
                                    <LazyLoadingFallback componentName="Analysis Panel" />
                                  }
                                >
                                  <AnalysisPanel
                                    key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                    panelVariant="desktop"
                                  />
                                </Suspense>
                              </LazyErrorBoundary>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Charts tab - kept mounted when hidden so grid state survives switches */}
                  {isChartsGridEnabled() && (
                    <div
                      className={cn(
                        'flex h-full w-full max-w-none min-h-0 min-w-0 flex-1 flex-col self-stretch overflow-hidden',
                        activeTab === 'charts' ? 'animate-in' : 'hidden',
                        activeTab !== 'charts' && 'pointer-events-none'
                      )}
                    >
                      <LazyErrorBoundary componentName="Charts Grid">
                        <ChartsGridTab
                          isActive={activeTab === 'charts'}
                          onChartClick={(ticker: string) => {
                            dispatch(setChartTicker(ticker));
                            dispatch(setSelectedTicker(ticker));
                            dispatch(requestDeepSubTab('chart'));
                            dispatch(requestNavigateMainTab('deep_analysis'));
                          }}
                        />
                      </LazyErrorBoundary>
                    </div>
                  )}

                  {/* Deep Analysis tab - container with Analysis + Selected Chart sub-tabs */}
                  <div
                    className={cn(
                      'flex h-full w-full max-w-none min-h-0 min-w-0 flex-1 flex-col self-stretch overflow-hidden',
                      activeTab === 'deep_analysis' ? 'animate-in' : 'hidden',
                      activeTab !== 'deep_analysis' && 'pointer-events-none'
                    )}
                  >
                    <LazyErrorBoundary componentName="Deep Analysis">
                      <Suspense
                        fallback={<LazyLoadingFallback componentName="Deep Analysis" />}
                      >
                        <DeepAnalysisContainer
                          isActive={activeTab === 'deep_analysis'}
                          subTab={deepSubTab}
                          setSubTab={setDeepSubTab}
                        />
                      </Suspense>
                    </LazyErrorBoundary>
                  </div>

                  {/* Alerts Tab - Beta: visible to all authenticated users (may contain bugs) */}
                  {activeTab === 'alerts' && isAuthenticated && (
                    <div className="animate-in flex min-h-0 flex-1 flex-col overflow-hidden">
                      <div className="flex min-h-0 flex-1 flex-col gap-2 md:flex-row md:gap-4">
                        {/* Left: Alerts Tab */}
                        <div
                          className={cn(
                            // flex-col so AlertsTab flex-1 + overflow-y-auto gets a real height budget (was block → no flex sizing → no scrollbar)
                            'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-all duration-300',
                            analysisPanel.isOpen
                              ? 'w-full md:w-1/2 md:min-w-[400px] max-md:min-w-0'
                              : 'w-full'
                          )}
                        >
                          <AlertsTab />
                        </div>

                        {/* Right: Analysis Panel (static on desktop; full-screen overlay on mobile when open) */}
                        {analysisPanel.isOpen && (
                          <>
                            <div className="fixed inset-0 z-50 flex flex-col bg-bg-primary md:hidden">
                              <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-surface-container-low/95 shrink-0 min-h-[48px]">
                                <button
                                  type="button"
                                  onClick={() => dispatch(closePanel())}
                                  className="flex items-center gap-2 text-text-primary hover:text-accent-primary min-h-[44px] min-w-[44px]"
                                  aria-label="Close panel"
                                >
                                  <i className="fas fa-arrow-left" />
                                  <span className="text-sm font-medium">
                                    Back
                                  </span>
                                </button>
                                <span className="text-sm font-semibold text-text-primary truncate max-w-[180px]">
                                  {analysisPanel.selectedTicker}
                                </span>
                                <div className="w-14" />
                              </div>
                              <div className="flex-1 overflow-auto">
                                <LazyErrorBoundary componentName="Analysis Panel">
                                  <Suspense
                                    fallback={
                                      <LazyLoadingFallback componentName="Analysis Panel" />
                                    }
                                  >
                                    <AnalysisPanel
                                      key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                      panelVariant="mobile"
                                    />
                                  </Suspense>
                                </LazyErrorBoundary>
                              </div>
                            </div>
                            <div className="flex min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border-subtle md:max-h-full md:w-1/2 md:min-w-[500px] max-md:hidden">
                              <LazyErrorBoundary componentName="Analysis Panel">
                                <Suspense
                                  fallback={
                                    <LazyLoadingFallback componentName="Analysis Panel" />
                                  }
                                >
                                  <AnalysisPanel
                                    key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                    panelVariant="desktop"
                                  />
                                </Suspense>
                              </LazyErrorBoundary>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Trade Diary Tab - Pro-gated, feature-flag gated */}
                  {activeTab === 'positions' && isAuthenticated && (
                    <div className="animate-in flex min-h-0 flex-1 flex-col overflow-hidden">
                      <LazyErrorBoundary componentName="Trade Diary">
                        <Suspense
                          fallback={<LazyLoadingFallback componentName="Trade Diary" />}
                        >
                          <DiaryPage />
                        </Suspense>
                      </LazyErrorBoundary>
                    </div>
                  )}

                  {/* Help Tab */}
                  {activeTab === 'help' && (
                    <div className="animate-in h-full min-h-0">
                      <HelpTab />
                    </div>
                  )}

                  {/* Trading Opportunities Tab */}
                  {activeTab === 'trading' && (
                    <div className="animate-in flex min-h-0 flex-1 flex-col md:overflow-hidden">
                      {/* Flex layout: Table on left, Analysis Panel on right - stack on mobile */}
                      <div className="flex min-h-0 flex-1 flex-col gap-2 md:flex-row md:gap-4">
                        {/* Left: Trading Opportunities */}
                        <div
                          className={cn(
                            'min-h-0 min-w-0 flex-1 overflow-hidden transition-all duration-300',
                            analysisPanel.isOpen
                              ? 'w-full md:w-1/2 md:min-w-[400px] max-md:min-w-0'
                              : 'w-full'
                          )}
                        >
                          <LazyErrorBoundary componentName="Trading Opportunities">
                            <Suspense
                              fallback={
                                <LazyLoadingFallback componentName="Trading Opportunities" />
                              }
                            >
                              <TradingOpportunitiesTab />
                            </Suspense>
                          </LazyErrorBoundary>
                        </div>

                        {/* Right: Analysis Panel (static on desktop; full-screen overlay on mobile when open) */}
                        {analysisPanel.isOpen && (
                          <>
                            <div className="fixed inset-0 z-50 flex flex-col bg-bg-primary md:hidden">
                              <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle bg-surface-container-low/95 shrink-0 min-h-[48px]">
                                <button
                                  type="button"
                                  onClick={() => dispatch(closePanel())}
                                  className="flex items-center gap-2 text-text-primary hover:text-accent-primary min-h-[44px] min-w-[44px]"
                                  aria-label="Close panel"
                                >
                                  <i className="fas fa-arrow-left" />
                                  <span className="text-sm font-medium">
                                    Back
                                  </span>
                                </button>
                                <span className="text-sm font-semibold text-text-primary truncate max-w-[180px]">
                                  {analysisPanel.selectedTicker}
                                </span>
                                <div className="w-14" />
                              </div>
                              <div className="flex-1 overflow-auto">
                                <LazyErrorBoundary componentName="Analysis Panel">
                                  <Suspense
                                    fallback={
                                      <LazyLoadingFallback componentName="Analysis Panel" />
                                    }
                                  >
                                    <AnalysisPanel
                                      key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                      panelVariant="mobile"
                                    />
                                  </Suspense>
                                </LazyErrorBoundary>
                              </div>
                            </div>
                            <div className="flex min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border-subtle md:max-h-full md:w-1/2 md:min-w-[500px] max-md:hidden">
                              <LazyErrorBoundary componentName="Analysis Panel">
                                <Suspense
                                  fallback={
                                    <LazyLoadingFallback componentName="Analysis Panel" />
                                  }
                                >
                                  <AnalysisPanel
                                    key={`analysis-panel-${analysisPanel.selectedTicker}`}
                                    panelVariant="desktop"
                                  />
                                </Suspense>
                              </LazyErrorBoundary>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Role Request Tab - for authenticated non-admin users */}
                  {activeTab === 'roles' &&
                    isAuthenticated &&
                    !canAccessAdmin && (
                      <div className="animate-in">
                        <LazyErrorBoundary componentName="User Role Request">
                          <Suspense
                            fallback={
                              <LazyLoadingFallback componentName="User Role Request" />
                            }
                          >
                            <UserRoleRequest />
                          </Suspense>
                        </LazyErrorBoundary>
                      </div>
                    )}

                  {/* Role Request Tab - for unauthenticated users, show sign-in message */}
                  {activeTab === 'roles' && !isAuthenticated && (
                    <div className="animate-in p-8 text-center">
                      <p className="text-text-secondary">
                        Sign in to request roles
                      </p>
                    </div>
                  )}

                  {/* Admin Hub Tab - for admin only; others see access message */}
                  {activeTab === 'admin' && canAccessAdmin && (
                    <div className="animate-in">
                      <LazyErrorBoundary componentName="Admin Hub">
                        <Suspense
                          fallback={
                            <LazyLoadingFallback componentName="Admin Hub" />
                          }
                        >
                          <AdminHub />
                        </Suspense>
                      </LazyErrorBoundary>
                    </div>
                  )}
                  {activeTab === 'admin' && !canAccessAdmin && (
                    <div className="animate-in p-8 text-center">
                      <p className="text-text-secondary">
                        Admin access required. Request roles or sign in with an
                        admin account.
                      </p>
                    </div>
                  )}

                  {/* Agent Console Tab - Beta: visible to all authenticated users (may contain bugs) */}
                  {activeTab === 'agents' && isAuthenticated && (
                    <div className="animate-in">
                      <LazyErrorBoundary componentName="Agent Console">
                        <Suspense
                          fallback={
                            <LazyLoadingFallback componentName="Agent Console" />
                          }
                        >
                          <AgentConsole />
                        </Suspense>
                      </LazyErrorBoundary>
                    </div>
                  )}

                  {/* Settings Tab - authenticated users only (plan/account/preferences) */}
                  {activeTab === 'settings' && isAuthenticated && (
                    <div className="animate-in">
                      <LazyErrorBoundary componentName="Settings">
                        <Suspense
                          fallback={<LazyLoadingFallback componentName="Settings" />}
                        >
                          <SettingsPage />
                        </Suspense>
                      </LazyErrorBoundary>
                    </div>
                  )}
                  {activeTab === 'settings' && !isAuthenticated && (
                    <div className="animate-in p-8 text-center">
                      <p className="text-text-secondary">
                        {t('settings.signInRequired', 'Sign in to access your settings.')}
                      </p>
                    </div>
                  )}

                  {/* Modals: mount lazy chunks only when open so refresh does not show many Suspense fallbacks */}
                  {modals.filtersModal && (
                    <LazyErrorBoundary componentName="Filters Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Filters Modal" />
                        }
                      >
                        <FiltersModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}

                  {modals.legacyFiltersModal && (
                    <LazyErrorBoundary componentName="Legacy Filters Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Legacy Filters Modal" />
                        }
                      >
                        <LegacyFiltersModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}

                  {modals.finvizScreenerModal && <FinvizScreenerModal />}

                  {modals.chartModal && (
                    <LazyErrorBoundary componentName="Chart Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Chart Modal" />
                        }
                      >
                        <ChartModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}

                  {modals.aiAnalysisModal && (
                    <LazyErrorBoundary componentName="AI Analysis Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="AI Analysis Modal" />
                        }
                      >
                        <AIAnalysisModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}

                  {modals.upgradePrompt && (
                    <LazyErrorBoundary componentName="Upgrade Prompt Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Upgrade Prompt Modal" />
                        }
                      >
                        <UpgradePromptModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}

                  {modals.aboutModal && (
                    <LazyErrorBoundary componentName="About Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="About Modal" />
                        }
                      >
                        <AboutModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                  {modals.contactSupportModal && (
                    <LazyErrorBoundary componentName="Contact Support Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Contact Support Modal" />
                        }
                      >
                        <ContactSupportModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                  {modals.feedbackModal && (
                    <LazyErrorBoundary componentName="Feedback Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Feedback Modal" />
                        }
                      >
                        <FeedbackModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                  {modals.termsModal && (
                    <LazyErrorBoundary componentName="Terms Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Terms Modal" />
                        }
                      >
                        <TermsModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                  {modals.privacyModal && (
                    <LazyErrorBoundary componentName="Privacy Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Privacy Modal" />
                        }
                      >
                        <PrivacyModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                  {modals.disclaimerModal && (
                    <LazyErrorBoundary componentName="Disclaimer Modal">
                      <Suspense
                        fallback={
                          <LazyLoadingFallback componentName="Disclaimer Modal" />
                        }
                      >
                        <DisclaimerModal />
                      </Suspense>
                    </LazyErrorBoundary>
                  )}
                </div>

                {/* Server-enforced disclaimer gate - blocking modal that
                    fires when /api/user/disclaimer-status returns
                    accepted=false. Closes the Google-signup race-bypass
                    at firebase/index.ts:337-340. The lazy DisclaimerModal
                    above is the user-triggered "view disclaimer" version;
                    this gate is the post-auth enforcement. */}
                <LazyErrorBoundary componentName="Disclaimer Gate">
                  <Suspense fallback={null}>
                    <DisclaimerGateModal isAuthenticated={isAuthenticated} />
                  </Suspense>
                </LazyErrorBoundary>

                {/* Post-trial paywall - self-hides unless billing.isLocked.
                    Backend (HTTP 402) is the real gate; this is the UX wall. */}
                <TrialExpiredWall suppressed={activeTab === 'settings'} />

                {/* Footer - above mobile bottom tabs.
                    Hidden on Deep Analysis → Live Chart sub-tab so the chart
                    gets ~50px more vertical (class-based collapse, not unmount). */}
                <Footer
                  hidden={
                    activeTab === 'deep_analysis' && deepSubTab === 'chart'
                  }
                />
              </main>

              {/* Mobile Bottom Navigation */}
              <MobileBottomTabs
                activeTab={activeTab}
                onTabChange={handleTabChange}
                isAuthenticated={isAuthenticated}
                isAdmin={canAccessAdmin}
              />
            </div>
            <CookieConsentBanner />
            <AccessibilityToolbar />
            {isHelpSystemEnabled() && (
              <>
                <HelpCenterModal />
                {!isTestEnv && (
                  <>
                    <OnboardingWelcomeModal />
                    <OnboardingChecklistCard />
                    <OnboardingTourOverlay />
                  </>
                )}
              </>
            )}
          </LanguageProvider>
        </AccessibilityProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
